import { createContext, useContext, useEffect, useState } from "react";
import {
  PublicClientApplication,
  EventType,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalAuthConfig } from "./msalAuthConfig";
import Loading from "../Common/Loading";

const msalInstance = new PublicClientApplication(msalAuthConfig);

const AuthContext = createContext();

export default msalInstance;

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [msalReady, setMsalReady] = useState(false);
  const [tokenCache, setTokenCache] = useState({ token: null, expiresOn: null });

  useEffect(() => {
    let callbackId;

    const init = async () => {
      await msalInstance.initialize();
      setMsalReady(true);

      callbackId = msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const authResult = event.payload;
          msalInstance.setActiveAccount(authResult.account);
          setAccount(authResult.account);
        }
      });

      await msalInstance.handleRedirectPromise();

      const active =
        msalInstance.getActiveAccount() ||
        msalInstance.getAllAccounts()[0] ||
        null;
      if (active) {
        msalInstance.setActiveAccount(active);
        setAccount(active);
      } else {
        msalInstance.loginRedirect();
      }
    };

    init();

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const login = () => msalInstance.loginRedirect();
  const logout = () => {
    setTokenCache({ token: null, expiresOn: null });
    msalInstance.logoutRedirect();
  };
  const isLoggedIn = !!account;

  const isTokenExpired = (expiresOn) => {
    if (!expiresOn) return true;
    // Refresh token 5 minutes before actual expiration
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return Date.now() >= (expiresOn.getTime() - bufferTime);
  };

  const acquireToken = async (forceRefresh = false) => {
    if (!account) {
      console.error("No account found. Redirecting to login...");
      msalInstance.loginRedirect();
      throw new Error("No account available");
    }

    // Return cached token if valid and not forcing refresh
    if (!forceRefresh && tokenCache.token && !isTokenExpired(tokenCache.expiresOn)) {
      return tokenCache.token;
    }

    const request = {
      account: account,
      forceRefresh: forceRefresh,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(request);
      if (!response || !response.idToken) {
        console.error("Token acquisition returned empty token");
        throw new Error("Empty token received");
      }
      
      // Cache the token with expiration
      setTokenCache({
        token: response.idToken,
        expiresOn: response.expiresOn
      });
      
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log("Interaction required. Redirecting to re-authenticate...");
        // Check if interaction is already in progress
        if (!msalInstance.getInteractionInProgress()) {
          msalInstance.loginRedirect(request);
          throw new Error("Redirecting for authentication");
        } else {
          console.warn("Interaction is already in progress. Please wait.");
          throw new Error("Authentication in progress");
        }
      }
      console.error("Token acquisition failed:", error);
      // Clear invalid token cache
      setTokenCache({ token: null, expiresOn: null });
      // If token acquisition fails, redirect to login
      msalInstance.loginRedirect(request);
      throw error;
    }
  };

  if (!msalReady) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider
      value={{ login, logout, isLoggedIn, account, acquireToken }}
    >
      <MsalProvider instance={msalInstance}>{children}</MsalProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
